import * as Hogan from 'hogan.js';
import {DisplayObject, Sprite} from 'pixi.js';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {
    CommandLineInterface,
    consoleLogger,
    CoreDI,
    DisplayObjectUtils, inject, injectable, LogType,
    ObjectBuilder,
    ResourceLoader,
    System
} from '@gongxigames/framework/core';
import {GameConfig, VersionsInfo} from '@gongxigames/framework/slot/types';
import {ArrayFilter} from '@gongxigames/framework/slot/services/logic/mathfilters/ArrayFilter';
import {AbstractFilter} from '@gongxigames/framework/slot/services/logic/mathfilters/AbstractFilter';
import {ReelConstants} from '@gongxigames/framework/slot/utils';
import {MatrixFilter} from '@gongxigames/framework/slot/services/logic/mathfilters/MatrixFilter';
import {WinTypeFilter} from '@gongxigames/framework/slot/services/logic/mathfilters/WinTypeFilter';
import {WinPresentationUtils} from '@gongxigames/framework/slot/services/WinPresentationUtils';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {SoundManager} from '@gongxigames/framework/slot/services/SoundManager';
import {SoundPlayer} from '@gongxigames/framework/slot/services/SoundPlayer';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {SpecialWinTypeConfig} from '@gongxigames/framework/slot/configs/SpecialWinTypeConfig';
import {ReelsetMatrix} from '@gongxigames/framework/slot/modules/cheatpage/ReelsetMatrix';
import {CheatConstants} from '@gongxigames/framework/slot/modules/cheatpage/CheatConstants';
import {ErrorsPopupPage} from '@gongxigames/framework/slot/modules/cheatpage/ErrorsPopupPage';
import {SessionsPage} from '@gongxigames/framework/slot/modules/cheatpage/SessionsPage';
import {CheatsModule} from '@gongxigames/framework/slotCommon/modules/cheats/CheatsModule';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

interface CheatData {
    menuItems: string[];
    filters: string[];
    demoFilters: string[];
    offlineFilters: string[];
    winFilters: string[];
}

/**
 * @deprecated This module is not supported, please use a new module CheatsModule
 */
@injectable()
export class CheatModule extends Module<View> {
    public static readonly NAME: symbol = CheatsModule.NAME;

    public static readonly MESSAGES_AMOUNT: number = 50;

    @inject(DI.WinPresentationUtils)
    protected _winPresentationUtils!: WinPresentationUtils;

    @inject(CoreDI.ResourceLoader)
    protected _resourceLoader!: ResourceLoader;

    @inject(CoreDI.System)
    protected _system!: System;

    @inject(DI.ResourceManager)
    protected _resourceManager!: ResourceManager;

    @inject(CoreDI.ObjectBuilder)
    protected _objectBuilder!: ObjectBuilder;

    @inject(DI.SoundManager)
    protected _soundManager!: SoundManager;

    @inject(DI.SoundPlayer)
    protected _soundPlayer!: SoundPlayer;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.DisplayObjectUtils)
    protected _displayObjectUtils!: DisplayObjectUtils;

    @inject(CoreDI.CLI)
    protected _cli!: CommandLineInterface;

    protected _cheatMenu!: HTMLElement;
    protected _menu!: HTMLElement;
    protected _activeFilters!: HTMLElement;
    protected _console!: HTMLElement;

    protected _chosenFilters: string[] = [];
    protected _pages!: NodeListOf<HTMLElement>;
    protected _data!: CheatData;

    protected _arrayFilter!: ArrayFilter;
    protected _filters: Record<string, AbstractFilter> = {};
    protected _demoFilters: Record<string, AbstractFilter> = {};
    protected _offlineFilters: Record<string, AbstractFilter> = {};

    protected _matrixSymbols: string[] = [];
    protected _matrix: number[][] = [];
    protected _isReset = false;
    protected _reelsetMatrix!: ReelsetMatrix;
    protected _resourcesPromise: any;
    protected keyDownListener: (e: KeyboardEvent) => void = this.onKeyDown.bind(this);
    protected keyUpListener: (e: KeyboardEvent) => void = this.onKeyUp.bind(this);

    public init(): void {
        if (this.isDisabled()) {
            return;
        }

        super.init();

        this.addFilters();

        this.createCheatMenu();

        this.addConsoleFunctionality();

        this.setCheatSettings();
    }

    public destroy(): void {
        super.destroy();

        document.removeEventListener('keydown', this.keyDownListener);
        document.removeEventListener('keyup', this.keyUpListener);
        document.getElementById('chosenFilters')?.remove();

        if (this._cheatMenu && this._cheatMenu.parentNode) {
            this._cheatMenu.remove();
        }
    }

    protected setCheatSettings(): void {
        if (this.settings.get('enableSpecialWinType')) {
            this.settings.set('specialWinTypeConfig', SpecialWinTypeConfig);
        }
    }

    protected setupViews(): void {
    }

    protected setupStates(): void {
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponse);
        this.on(LogicEvents.FREE_SPIN_RESPONSE, this.onSpinResponse);
        this.on(LogicEvents.RESPIN_RESPONSE, this.onSpinResponse);
        this.on(LogicEvents.MINI_SPIN_RESPONSE, this.onSpinResponse);
        this.on(LogicEvents.SELECT_RESPONSE, this.onSpinResponse);

        this._resourcesPromise = new Promise(resolve => this.on('General.ResourcesLoading.LOADED_ALL_RESOURCES', resolve));

        this._eventManager.on('CheatModule.addFilters', this.selectFiltersByName.bind(this), this);
        this._eventManager.on('CheatModule.removeFilters', this.removeFiltersByName.bind(this), this);
        this._eventManager.on('CheatModule.clearFilters', this.clearFilters.bind(this), this);
        this._eventManager.on('CheatModule.addStopPositions', this._addStopPositions.bind(this), this);
        this._eventManager.on('CheatModule.clearStopPositions', this._clearStopPositions.bind(this), this);
    }

    protected onSpinResponse(): void {
        this._reelsetMatrix && this._reelsetMatrix.getReelStrips();
    }

    protected onResized(): void {
        if (this._cheatMenu) {
            this._cheatMenu.style.height = '';

            if (this._cheatMenu.clientHeight > document.documentElement.clientHeight) {
                this._cheatMenu.style.height = document.documentElement.clientHeight + 'px';
            }
        }
    }

    protected initData(): void {
        this._data = {
            menuItems: CheatConstants.PAGES,
            filters: Object.keys(this._filters),
            demoFilters: Object.keys(this._demoFilters),
            offlineFilters: Object.keys(this._offlineFilters),
            winFilters: CheatConstants.WIN_TYPES
        };
    }

    protected initialize(): void {
        this.initElements();
        this.initMenuEvents();
        this.initInfo();
        this.initConsole();
        this.initFilterEvents('customFilter');
        this.initFilterEvents('offlineFilter');
        this.initWinTypeFilterEvents();
        this.initMatrix();
        this.initReelsetMatrix();
        this.initSessions();
        this.initErrorsPopupPage();
        this.initSoundsToolsPage();

        if (Object.keys(this._offlineFilters).length === 0) {
            document.getElementById('offline')!.style.display = 'none';
        }

        // For AF: pointer when filters are ready and can be set.
        this._eventManager.emit('CheatModule.initialized');
    }

    protected initElements(): void {
        this._cheatMenu = document.getElementById('cheatMenu')!;
        this._menu = document.getElementById('menu')!;
        this._pages = document.querySelectorAll<HTMLElement>('#menu, #pages > div');
        this._activeFilters = document.getElementById('activeFilters')!;
        this._console = document.getElementById('console')!;
    }

    protected onAllRecourcesLoaded(): void {
        this.initSoundsToolsPage();
    }

    protected initMenuEvents(): void {
        const titles = document.querySelectorAll('#cheatMenu .title') as any;

        for (const title of titles) {
            title.onclick = () => this.hideCheatMenu();
        }

        document.getElementById('return')!.onclick = () => this.backToCheatMenu();

        const menuItems = document.querySelectorAll('#menu li') as any;

        for (const item of menuItems) {
            item.onclick = () => {
                this.closeAllPages();
                document.getElementById(item.className)!.classList.add('active');
            };
        }

        if (this._settings.get('platform') === 'desktop') {
            this.initButtonForMenu();
        } else {
            this.initGesturesForMenu();
        }

        this.initToggleButtons();
    }

    protected openCheatMenu(): void {
        this._cheatMenu.classList.add('active');
        this._menu.classList.add('active');
        this.onResized();
    }

    protected hideCheatMenu(): void {
        this.closeAllPages();
        this._cheatMenu.classList.remove('active');
    }

    protected backToCheatMenu(): void {
        this.closeAllPages();
        this._menu.classList.add('active');
    }

    protected closeAllPages(): void {
        this._pages.forEach((page) => {
            page.classList.remove('active');
        });
    }

    protected initButtonForMenu(): void {
        document.addEventListener('keydown', this.keyDownListener);
        document.addEventListener('keyup', this.keyUpListener);
    }

    protected onKeyDown(e: KeyboardEvent): void {
        if (e.keyCode === CheatConstants.BUTTON_MENU) {
            if (this._cheatMenu.classList.contains('active')) {
                this.hideCheatMenu();
            } else {
                this.openCheatMenu();
            }
        } else if (e.keyCode === CheatConstants.BUTTON_RESET) {
            this._isReset = true;
        }
    }

    protected onKeyUp(e: KeyboardEvent): void {
        if (e.keyCode === CheatConstants.BUTTON_RESET) {
            this._isReset = false;
        }
    }

    protected initGesturesForMenu(): void {
        let startX = 0;
        let endX = 0;

        document.querySelector<HTMLElement>('body > canvas')!
            .addEventListener('touchstart', (e: any) => startX = e.targetTouches[0].clientX);

        document.querySelector<HTMLElement>('body > canvas')!.addEventListener('touchend', (e: any) => {
            endX = e.changedTouches[0].clientX;

            if (endX - startX > CheatConstants.MOVING_DISTANCE) {
                this.openCheatMenu();
            } else if (startX - endX > CheatConstants.MOVING_DISTANCE) {
                this.hideCheatMenu();
            }
            startX = 0;
            endX = 0;
        });
    }

    protected initInfo(): void {
        const gameInfo = document.getElementById('gameInformation') as any;
        const {versions} = window.RP_CONFIG.buildProperty;

        for (const key in versions) {
            if (!versions.hasOwnProperty(key)) {
                continue;
            }

            const element = document.createElement('p');

            element.innerHTML = `${key}:${versions[key as keyof VersionsInfo]}`;

            gameInfo.appendChild(element);
        }

        const client = document.querySelector('.client') as any;

        client.innerHTML = 'Client: ' + navigator.appVersion;

        const resolution = document.querySelector('.resolution') as any;

        resolution.innerHTML = `Resolution: ${window.devicePixelRatio * window.screen.width}/` +
            `${window.devicePixelRatio * window.screen.height}`;

        const clearBtn = document.getElementById('clearBtn')!;

        clearBtn.onclick = () => this._console.innerHTML = '';

        const saveBtn = document.getElementById('saveBtn')!;

        saveBtn.onclick = () => {
            const selection = window.getSelection()!;
            const range = document.createRange();
            const copiedText = document.querySelector('#gameInfo')!;

            document.body.classList.toggle('canSelect');
            selection.removeAllRanges();
            range.selectNode(copiedText);
            selection.addRange(range);
            try {
                document.execCommand('copy');
                document.body.classList.toggle('canSelect');
            } catch (err) {
                consoleLogger.log('Unable to copy, update your browser');
            }
        };
    }

    protected initConsole(): void {
        const logTypeMap: Record<string, string> = {
            [LogType.Error]: 'error',
            [LogType.Warning]: 'warn'
        };

        consoleLogger.addReceiver((type: LogType, data) => {
            const className = logTypeMap[type] || '';
            const message = consoleLogger.stringifyObjects(data);

            this._console.innerHTML += `<p class='${className}'>${message}</p>`;
            this.cleanConsole();
        });
    }

    protected cleanConsole(): void {
        if (this._console.children.length > CheatModule.MESSAGES_AMOUNT) {
            this._console.removeChild(this._console.children[0]);
        }
    }

    protected initWinTypeFilterEvents(): void {
        const winFilters = Array.from(document.querySelectorAll<HTMLElement>('.winFilter'));

        winFilters.forEach(element => {
            element.onclick = () => {
                const eponymousFilters = winFilters.filter(f => f.getAttribute('name') === element.getAttribute('name'));

                if (element.classList.contains('active')) {
                    eponymousFilters.forEach(f => f.classList.remove('active'));
                } else {
                    const activeFilters = winFilters.filter(f => f.classList.contains('active'));

                    activeFilters.forEach(f => f.classList.remove('active'));
                    eponymousFilters.forEach(f => f.classList.add('active'));
                }
                this.setActiveFilters();
            };

        });
    }

    protected initFilterEvents(filterType: string): void {
        const filters = document.querySelectorAll(`.${filterType}`) as any;

        for (const filter of filters) {
            filter.onclick = () => {
                filter.classList.toggle('active');
                this.setActiveFilters();
            };
        }
    }

    protected selectFiltersByName(filterNames: string | string[]): void {
        const filters = document.querySelectorAll('.customFilter, .offlineFilter, .winFilter') as any;

        [...filterNames].forEach(filterName => {
            this.addActiveClassForFilter(filters, filterName);
        });
    }

    protected removeFiltersByName(filterNames: string | string[]): void {
        const filters = document.querySelectorAll('.customFilter, .offlineFilter, .winFilter') as any;

        [...filterNames].forEach(filterName => {
            this.removeActiveClassForFilter(filters, filterName);
        });
    }

    protected clearFilters(): void {
        Array.from(document.querySelectorAll('.filter.active'))?.forEach(elem => elem.classList.remove('active'));
        document.getElementById('activeFilters')!.innerHTML = '';

        this._arrayFilter?.removeFilters();

        this.logic.setFilter(null as any);

        this._chosenFilters.length = 0;
    }

    protected initMatrix(): void {
        const reelset = document.getElementById('reelset')!;
        const reelsAmount = ReelConstants.REELS_AMOUNT;
        const symbolsAmount = ReelConstants.ROW_AMOUNT;

        this._matrixSymbols = this.addSymbols();

        reelset.className = 'reels-' + reelsAmount;

        for (let i = 0; i < reelsAmount; i++) {
            const reel = document.createElement('div');

            this._matrix[i] = [];

            reel.className = 'reel reel' + i;
            for (let j = 0; j < symbolsAmount; j++) {
                this.addSymbolToMatrix(reel, i, j);
            }
            reelset.appendChild(reel);
        }

        this.initMatrixSymbols();
    }

    protected initReelsetMatrix(): void {
        this._reelsetMatrix = new ReelsetMatrix(this);
    }

    protected addSymbolToMatrix(reel: HTMLElement, reelNumber: number, rowNumber: number): void {
        const symbol = document.createElement('div');
        const emptyImage = document.createElement('div');

        emptyImage.className = 'picked';
        symbol.appendChild(emptyImage);

        for (const sym of this._matrixSymbols) {
            if (sym) {
                const image = document.createElement('img');

                image.className = sym;
                symbol.appendChild(image);
            }
        }
        symbol.className = `symbol reel-${reelNumber} row-${rowNumber}`;

        reel.appendChild(symbol);
    }

    protected initMatrixSymbols(): void {
        const pickedSymbols = document.querySelectorAll('#reelset .picked') as any;

        for (const symbol of pickedSymbols) {
            symbol.onclick = (e: any) => {
                e.target.classList.remove('picked');

                if (this._isReset) {
                    e.target.parentElement.children[0].classList.add('picked');
                } else {
                    const nextSiblinng = e.target.nextElementSibling;

                    if (nextSiblinng) {
                        if (!e.target.nextElementSibling.src) {
                            nextSiblinng.src = this.getImageSource(nextSiblinng.className);
                        }

                        nextSiblinng.classList.add('picked');
                    } else {
                        e.target.parentElement.children[0].classList.add('picked');
                    }
                }
                this.initMatrixSymbols();
                this.initMatrixButtons();
            };
        }
    }

    protected initMatrixButtons(): void {
        this.applyMatrixFilter();
        this.resetMatrixFilter();
    }

    protected resetMatrixFilter(): void {
        const rejectBtn = document.getElementById('rejectBtn')!;

        rejectBtn.onclick = () => {
            const chosenFilters = document.querySelectorAll('#activeFilters p') as any;

            for (const chosenFilter of chosenFilters) {
                if (chosenFilter.innerHTML === 'matrixFilter') {
                    chosenFilter.parentNode.removeChild(chosenFilter);
                }
            }

            this.resetMatrixSymbols();
        };
    }

    protected resetMatrixSymbols(): void {
        const allSymbols = document.querySelectorAll('.symbol') as any;
        const pickedSymbols = document.querySelectorAll('.picked') as any;

        for (const item of pickedSymbols) {
            item.classList.remove('picked');
        }

        for (const symbol of allSymbols) {
            symbol.children[0].classList.add('picked');
        }

        for (const reel of this._matrix) {
            reel.length = 0;
        }
    }

    protected applyMatrixFilter(): void {
        const applyBtn = document.getElementById('applyBtn')!;

        applyBtn.onclick = () => {
            const pickedSymbols = document.querySelectorAll('.picked') as any;

            for (const item of pickedSymbols) {
                if (item.classList[0] !== 'picked') {
                    const parentClasses = item.parentElement.classList;
                    const reel = parentClasses[1][5];
                    const row = parentClasses[2][4];

                    this._matrix[reel][row] = this._matrixSymbols.indexOf(item.classList[0]);
                }
            }

            this.setActiveFilters();
        };
    }

    protected setActiveFilters(): void {
        this.resetPreviousFilters();

        const winTypeFilter = document.querySelector('.winFilter.active') as any;

        for (const filter of this._data.winFilters) {
            if (winTypeFilter && filter === winTypeFilter.innerText) {
                this.addActiveFilter(filter);
                this._arrayFilter.addFilter(new WinTypeFilter(filter, this._gameConfig.winConfig.normal.winTypes));
            }
        }

        const customFilters = document.querySelectorAll('.customFilter.active') as any;
        const offlineFilters = document.querySelectorAll('.offlineFilter.active') as any;

        this.addFilterToArrayFilter(customFilters, false);
        this.addFilterToArrayFilter(offlineFilters, true);

        let isMatrixEmpty = true;

        for (const reel of this._matrix) {
            if (reel.length !== 0) {
                isMatrixEmpty = false;
            }
        }

        if (!isMatrixEmpty) {
            this.addActiveFilter('matrixFilter');
            this._arrayFilter.addFilter(new MatrixFilter('matrixFilter', this._matrix));
        }

        this.showActiveFilters();
        this.initEventsForActiveFilters();
        this.setArrayFilter();
    }

    protected addFilterToArrayFilter(activeFilters: any, isOffline: boolean): void {
        const filters = isOffline ? this._offlineFilters : this._filters;

        for (const filter of activeFilters) {
            if (filters[filter.innerText]) {
                this.addActiveFilter(filter.innerText);
                this._arrayFilter.addFilter(filters[filter.innerText]);
            }
        }
    }

    protected setArrayFilter(): void {
        this.logic.setFilter((!this._arrayFilter.isEmpty() ? this._arrayFilter : null) as any);
    }

    protected resetPreviousFilters(): void {
        this._arrayFilter = new ArrayFilter();
        this._chosenFilters = [];
        this._activeFilters.innerHTML = '';
    }

    protected addActiveFilter(filter: string): void {
        !this._chosenFilters.includes(filter) && this._chosenFilters.push(filter);
    }

    protected showActiveFilters(): void {
        for (const filter of this._chosenFilters) {
            this._activeFilters.innerHTML += `<p>${filter}</p>`;
        }
    }

    protected initEventsForActiveFilters(): void {
        const chosenFilters = document.querySelectorAll('#activeFilters p') as any;

        for (const chosenFilter of chosenFilters) {
            chosenFilter.onclick = () => {
                const activeFilters = document.querySelectorAll('.filter.active') as any;

                for (const activeFilter of activeFilters) {
                    if (chosenFilter.innerHTML === activeFilter.innerHTML) {
                        activeFilter.classList.remove('active');
                    }
                }

                if (chosenFilter.innerHTML === 'matrixFilter') {
                    this.resetMatrixSymbols();
                }

                this._arrayFilter.removeFilter(this._chosenFilters.indexOf(chosenFilter.innerText));
                this._chosenFilters.splice(this._chosenFilters.indexOf(chosenFilter.innerText), 1);
                chosenFilter.parentNode.removeChild(chosenFilter);
                this.setArrayFilter();
            };
        }
    }

    protected initSessions(): void {
        new SessionsPage(this._gameConfig.gameName, this.logic);
    }

    protected initSoundsToolsPage(): void {
        // if (this._resourceLoader.resources.sounds && Loader!.shared!.resources!.sounds.data) {
        // new SoundsToolsPage({
        //     sounds: this._resourceLoader.resources.sounds,
        //     soundsConfig: Loader.shared.resources.sounds.data,
        //     soundManager: this._soundManager,
        //     soundPlayer: this._soundPlayer,
        //     gameName: this._gameConfig.gameName
        // });
        // }
    }

    protected initErrorsPopupPage(): void {
        new ErrorsPopupPage(this._eventManager);
    }

    protected initToggleButtons(): void {
        const toggleStats = document.getElementById('toggleStats') as HTMLElement;
        const stats = document.getElementById('debugStats') as HTMLElement;
        const toggleRewind = document.getElementById('toggleRewind') as HTMLElement;
        const rewind = document.getElementById('rewindContainer') as HTMLElement;
        const replayLastSpin = document.getElementById('replayLastSpin') as HTMLElement;

        toggleStats.onclick = () => {
            if (stats) {
                let {display} = stats.style;

                display ||= 'block';
                stats.style.display = display === 'block' ? 'none' : 'block';
                toggleStats.classList.toggle('active');
            }
        };

        toggleRewind.onclick = () => {
            rewind.classList.toggle('hidden');
            toggleRewind.classList.toggle('active');
        };

        replayLastSpin.onclick = () => {
            const isActive = !replayLastSpin.classList.toggle('active');

            if (isActive) {
                const filterElem = document.getElementById('reelsetFilter')!;

                filterElem.innerHTML = '';
                this.logic.setFilterForStopPositions(null);
            } else {
                const stopPositions: number[] = this.logic.getFinalSpinStopPositions();

                this.logic.setFilterForStopPositions(stopPositions);
            }
        };
    }

    protected createCheatMenu(): void {
        this.initData();

        const onLoadedChatMenuTemplate = (template: string): void => {
            const html = Hogan.compile(template).render(this._data);

            document.getElementsByTagName('canvas')[0].insertAdjacentHTML('afterend', html);
        };

        this._resourceManager
            .loadTemplate('cheatMenu')
            .then(onLoadedChatMenuTemplate)
            .then(this.createPages.bind(this));
    }

    protected createPages(): void {
        const onLoadedPageTemplate = (template: string, page: string): void => {
            const data = page === 'filters' || page === 'AMDemo' ? this._data : {};
            const html = Hogan.compile(template).render(data);

            document.getElementById(page)?.insertAdjacentHTML('afterbegin', html);
        };

        const promises = CheatConstants.PAGES.map(page => {
            return this._resourceManager
                .loadTemplate(page)
                .then(template => onLoadedPageTemplate(template, page));
        });

        promises.push(this._resourcesPromise);

        Promise.all(promises).then(this.initialize.bind(this));
    }

    protected addFilters(): void {
    }

    protected addFilter(filter: AbstractFilter): void {
        this._filters[filter.name] = filter;
    }

    protected addOfflineFilter(filter: AbstractFilter): void {
        this._offlineFilters[filter.name] = filter;
    }

    protected addDemoFilter(filter: AbstractFilter): void {
        this._demoFilters[filter.name] = filter;
    }

    protected addSymbols(): string[] {
        return [];
    }

    public getImagesData(): any {
        return {};
    }

    protected isDisabled(): boolean {
        return window.location.search.indexOf('&disableCheats=true') >= 0;
    }

    protected addConsoleFunctionality(): void {
        this._cli.addCommand('sendCombo', (...positions: number[]) => this._addStopPositions(positions));
        this._cli.addCommand('resetCombo', () => this._clearStopPositions());
        this._cli.addCommand('getFilters', () => Object.keys(this._filters).forEach(filter => consoleLogger.log(filter)));
        this._cli.addCommand('getOfflineFilters', () => Object.keys(this._offlineFilters).forEach(filter => consoleLogger.log(filter)));
        this._cli.addCommand('setFilter', this.selectFiltersByName.bind(this));
        this._cli.addCommand('removeFilter', this.removeFiltersByName.bind(this));
        this._cli.addCommand('removeFilters', this.clearFilters.bind(this));
        this._cli.addCommand('lastPosition', () => this._setFinalSpinStopPositions());
    }

    protected addActiveClassForFilter(activeFilters: any, name: string): void {
        for (const filter of activeFilters) {
            if (filter.innerHTML === name) {
                filter.classList.add('active');
                this.setActiveFilters();
            }
        }
    }

    protected removeActiveClassForFilter(activeFilters: any, name: string): void {
        for (const filter of activeFilters) {
            if (filter.innerHTML === name) {
                filter.classList.remove('active');
                this.setActiveFilters();
            }
        }
    }

    private _addStopPositions(positions: number[]): void {
        const filterElem = document.getElementById('reelsetFilter')!;

        filterElem.innerHTML = `<p>Stop positions: ${positions}</p>`;

        this.logic.setFilterForStopPositions(positions);
    }

    private _setFinalSpinStopPositions(): void {
        const positions = this.logic.getFinalSpinStopPositions();

        this._clearStopPositions();
        this._addStopPositions(positions);
    }

    private _clearStopPositions(): void {
        const filterElem = document.getElementById('reelsetFilter')!;

        filterElem.innerHTML = '';
        this.logic.setFilterForStopPositions(null);
    }

    public getImageSource(symbol: string): string {
        return this.displayObjectToUrl(new Sprite(this._resourceLoader.getTexture(symbol)));
    }

    public displayObjectToUrl(obj: DisplayObject): string {
        return this._system.app.renderer.plugins.extract.base64(obj);
    }

    public get resourceLoader(): ResourceLoader {
        return this._resourceLoader;
    }

    public get displayObjectUtils(): DisplayObjectUtils {
        return this._displayObjectUtils;
    }
}
